import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Session } from '@supabase/supabase-js';
import { LoginResponse } from '../types/auth';

interface AuthUser {
  id: string;
  username: string;
  name: string;
  email: string;
  user_type: 'admin' | 'student' | 'adult' | 'minor' | 'institution';
  is_verified: boolean;
  birthdate?: string;
  gender?: 'male' | 'female' | null;
  phone?: string;
  guardian_phone?: string;
  address?: string;
  address_detail?: string;
  institution_name?: string;
  institution_type?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  session: Session | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; user?: AuthUser }>;
  logout: () => Promise<void>;
  setSession: (session: Session, user: AuthUser) => void;
  updateProfile: (updates: Partial<AuthUser>) => Promise<{ success: boolean; message?: string }>;
  isAuthenticated: boolean;
  isStudent: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSessionState] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Supabase 세션 초기화 및 변경 감지
  useEffect(() => {
    // 현재 세션 확인
    // catch가 없으면 세션 복원이 실패하는 순간 loading이 영원히 true로 남는다.
    // 그러면 마이페이지 같은 화면은 "불러오는 중..." 스피너에서 빠져나오지 못한다.
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setSessionState(session);
        if (session) {
          loadUserProfile(session.user.id);
        } else {
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error('Error restoring session:', error);
        setSessionState(null);
        setUser(null);
        setLoading(false);
      });

    // 세션 변경 감지
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSessionState(session);
      if (session) {
        loadUserProfile(session.user.id);
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // profiles 테이블에서 사용자 정보 로드
  const loadUserProfile = async (userId: string) => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('id, username, name, email, user_type, is_verified, birthdate, gender, phone, guardian_phone, address, address_detail, institution_name, institution_type')
        .eq('id', userId)
        .single();

      if (error) throw error;

      if (profile) {
        setUser({
          id: profile.id,
          username: profile.username,
          name: profile.name,
          email: profile.email,
          user_type: profile.user_type,
          is_verified: profile.is_verified,
          birthdate: profile.birthdate,
          gender: profile.gender,
          phone: profile.phone,
          guardian_phone: profile.guardian_phone,
          address: profile.address,
          address_detail: profile.address_detail,
          institution_name: profile.institution_name,
          institution_type: profile.institution_type,
        });
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // 로그인 (Edge Function 사용)
  const login = async (username: string, password: string): Promise<{ success: boolean; user?: AuthUser }> => {
    try {
      const { data, error } = await supabase.functions.invoke<LoginResponse>('login', {
        body: { username, password },
      });

      if (error) {
        console.error('Edge Function error:', error);
        return { success: false };
      }

      if (!data?.success) {
        console.error('Login failed:', data?.message);
        return { success: false };
      }

      // 세션 설정
      if (data.session) {
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
      }

      // 사용자 정보 설정
      let authUser: AuthUser | undefined;
      if (data.user) {
        authUser = {
          id: data.user.id,
          username: data.user.username,
          name: data.user.name,
          email: data.user.email,
          user_type: data.user.user_type,
          is_verified: data.user.is_verified,
        };
        setUser(authUser);
      }

      return { success: true, user: authUser };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false };
    }
  };

  // 로그아웃
  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSessionState(null);
  };

  // 외부에서 세션 설정 (회원가입 후 자동 로그인용)
  const setSession = (newSession: Session, newUser: AuthUser) => {
    setSessionState(newSession);
    setUser(newUser);
  };

  // 프로필 정보 업데이트
  const updateProfile = async (updates: Partial<AuthUser>): Promise<{ success: boolean; message?: string }> => {
    if (!user) {
      return { success: false, message: '로그인이 필요합니다.' };
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          email: updates.email,
          address: updates.address,
          address_detail: updates.address_detail,
        })
        .eq('id', user.id);

      if (error) throw error;

      // 로컬 상태 업데이트
      setUser({ ...user, ...updates });

      return { success: true, message: '저장되었습니다.' };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { success: false, message: '프로필 업데이트에 실패했습니다.' };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        login,
        logout,
        setSession,
        updateProfile,
        isAuthenticated: !!user,
        isStudent: user?.user_type === 'student' || user?.user_type === 'adult' || user?.user_type === 'minor',
        isAdmin: user?.user_type === 'admin',
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
